/**
 * Query key helpers for the react-query cache (COL-88).
 *
 * SECURITY-CRITICAL: every key is namespaced by the *authenticated* user id
 * (JWT `sub`, from AuthContext's `user.id` — never a value the caller could
 * spoof from a form field or URL). This is layer 1 of two independent
 * defenses against serving one user's cached data to another after a
 * session change:
 *
 *   1. Per-user key namespace (this file) — even if the cache were never
 *      cleared, user B's queries simply miss and refetch under their own
 *      namespace; they can never read a cache entry written for user A.
 *   2. `queryClient.clear()` on any detected change of `user.id`, wired in
 *      AuthContext.jsx — belt-and-braces in case a hook is ever added that
 *      forgets to scope its key (see AuthContext.jsx for the effect).
 *
 * `userId` is `undefined` while auth is still resolving, and every key falls
 * back to the literal `'anon'` in that case — a namespace SHARED BY EVERY
 * USER, unlike every other namespace here. That's only safe because nothing
 * ever actually writes to it, on two independent grounds:
 *   - Every page that calls one of these key builders is rendered behind
 *     `ProtectedRoute` (App.jsx), which shows a skeleton and does not mount
 *     the page at all until `AuthContext` has resolved `isAuthenticated`
 *     — and `isAuthenticated`/`user` are always set together, atomically,
 *     inside `checkAuth()`. There is no render of a query-bearing page with
 *     `isAuthenticated === true` and `user.id` still unset.
 *   - Belt-and-braces: every `useQuery`/`useInfiniteQuery` call site also
 *     passes `enabled: !!user?.id` explicitly, so even a future page that
 *     forgets to sit behind `ProtectedRoute` (or a test that mounts a page
 *     directly with a mocked, id-less `user`) still can't populate `'anon'`
 *     with real fetched data — the queryFn simply never runs.
 * If a future change ever needs a query to legitimately run pre-auth, do
 * not relax this — give that query its own explicit, clearly-named key
 * instead of loosening the shared `'anon'` fallback.
 */
function scope(userId) {
  return userId || 'anon';
}

export const queryKeys = {
  categories: (userId) => [scope(userId), 'categories'],
  items: (userId, params = {}) => [scope(userId), 'items', 'list', params],
  item: (userId, itemId) => [scope(userId), 'items', 'detail', itemId],
  entityTypeCounts: (userId) => [scope(userId), 'enrichments', 'counts'],
  enrichments: (userId, params = {}) => [scope(userId), 'enrichments', 'list', params],
  placeGeoCounts: (userId) => [scope(userId), 'enrichments', 'places', 'geo-counts'],
  placeEnrichments: (userId) => [scope(userId), 'enrichments', 'places', 'all'],
  // COL-94: eager fetch-all for entity grouping (mirrors placeEnrichments) —
  // params keyed in since, unlike places, the filter (entityType/entitySubtype)
  // actually varies per sidebar selection.
  allEnrichments: (userId, params = {}) => [scope(userId), 'enrichments', 'all', params],
  myCollections: (userId) => [scope(userId), 'collections', 'mine'],
  collection: (userId, collectionId) => [scope(userId), 'collections', 'detail', collectionId],
  collectionsForItem: (userId, itemId, itemType) => [scope(userId), 'collections', 'for-item', itemType, itemId],
};
